/**
 * Algolia Cache Configuration
 * Provides centralized caching configuration for all Algolia clients.
 *
 * Uses localStorage for response caching with a 30-minute TTL.
 * Bump CACHE_VERSION to invalidate all users' caches after updates.
 *
 * Cache entry format: { __ts: timestamp, __data: payload }
 *
 * @see https://www.algolia.com/doc/libraries/sdk/caching
 */
const AlgoliaCacheConfig = {
  // Bump this to invalidate all cached responses
  CACHE_VERSION: 1,

  // Algolia App ID (used in cache key)
  APP_ID: "RVBE1UCZU9",

  // Cache TTL in milliseconds (30 minutes)
  TTL_MS: 30 * 60 * 1000,

  /**
   * Check if cache should be bypassed via URL param
   * @returns {boolean}
   */
  shouldBypassCache() {
    const params = new URLSearchParams(window.location.search);
    return params.has("nocache");
  },

  /**
   * Get the cache key for localStorage
   * @returns {string}
   */
  getCacheKey() {
    return `algolia-responses-v${this.CACHE_VERSION}-${this.APP_ID}`;
  },

  /**
   * Check if a cache entry has expired
   * @param {object} entry - Cache entry with __ts timestamp
   * @returns {boolean}
   */
  isExpired(entry) {
    if (!entry || typeof entry.__ts !== "number") return false;
    return Date.now() - entry.__ts > this.TTL_MS;
  },

  /**
   * Create a localStorage cache for Algolia responses
   * Falls back to in-memory cache if localStorage unavailable
   * @returns {object} Cache object compatible with Algolia SDK
   */
  createResponsesCache() {
    const cacheKey = this.getCacheKey();

    if (this.shouldBypassCache()) {
      return this.createNullCache();
    }

    try {
      if (typeof localStorage === "undefined") {
        return this.createMemoryCache();
      }
      localStorage.setItem("__algolia_test__", "1");
      localStorage.removeItem("__algolia_test__");

      const cache = this.createLocalStorageCache(cacheKey);
      this.evictExpired(cacheKey);
      return cache;
    } catch (e) {
      return this.createMemoryCache();
    }
  },

  /**
   * Sweep expired entries from localStorage on init
   * @param {string} key - Cache key prefix
   */
  evictExpired(key) {
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (storageKey && storageKey.startsWith(key)) {
          try {
            const stored = JSON.parse(localStorage.getItem(storageKey));
            if (this.isExpired(stored)) {
              keysToRemove.push(storageKey);
            }
          } catch (e) {
            keysToRemove.push(storageKey);
          }
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      // Silently fail — eviction is best-effort
    }
  },

  /**
   * Create localStorage-based cache with TTL
   * @param {string} key - Cache key prefix
   * @returns {object} Cache object
   */
  createLocalStorageCache(key) {
    const self = this;
    return {
      get(cacheKey, defaultValue, events = {}) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          const stored = localStorage.getItem(`${key}_${keyStr}`);

          if (stored !== null) {
            try {
              const entry = JSON.parse(stored);

              // TTL check
              if (self.isExpired(entry)) {
                localStorage.removeItem(`${key}_${keyStr}`);
                // Fall through to cache miss
              } else {
                // Return cached data (unwrap TTL envelope)
                return typeof entry.__data !== "undefined"
                  ? entry.__data
                  : entry;
              }
            } catch (e) {
              localStorage.removeItem(`${key}_${keyStr}`);
            }
          }

          const value =
            typeof defaultValue === "function" ? defaultValue() : defaultValue;

          return Promise.resolve(value).then((resolvedValue) => {
            if (events.miss) {
              events.miss(resolvedValue);
            }
            return resolvedValue;
          });
        });
      },

      set(cacheKey, value) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          const entry = { __ts: Date.now(), __data: value };
          try {
            localStorage.setItem(`${key}_${keyStr}`, JSON.stringify(entry));
          } catch (e) {
            if (e.name === "QuotaExceededError") {
              self.clearAll();
              try {
                localStorage.setItem(
                  `${key}_${keyStr}`,
                  JSON.stringify(entry),
                );
              } catch (retryError) {
                // Silently fail
              }
            }
          }
          return value;
        });
      },

      delete(cacheKey) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          localStorage.removeItem(`${key}_${keyStr}`);
        });
      },

      clear() {
        return Promise.resolve().then(() => {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const storageKey = localStorage.key(i);
            if (storageKey && storageKey.startsWith(key)) {
              keysToRemove.push(storageKey);
            }
          }
          keysToRemove.forEach((k) => localStorage.removeItem(k));
        });
      },
    };
  },

  /**
   * Create in-memory cache with TTL (fallback when localStorage unavailable)
   * @returns {object} Cache object
   */
  createMemoryCache() {
    const cache = {};
    const self = this;
    return {
      get(cacheKey, defaultValue, events = {}) {
        return Promise.resolve().then(() => {
          const keyStr = JSON.stringify(cacheKey);
          if (keyStr in cache) {
            const entry = cache[keyStr];
            if (self.isExpired(entry)) {
              delete cache[keyStr];
            } else {
              return typeof entry.__data !== "undefined"
                ? entry.__data
                : entry;
            }
          }
          const value =
            typeof defaultValue === "function" ? defaultValue() : defaultValue;
          return Promise.resolve(value).then((resolvedValue) => {
            if (events.miss) {
              events.miss(resolvedValue);
            }
            return resolvedValue;
          });
        });
      },
      set(cacheKey, value) {
        return Promise.resolve().then(() => {
          cache[JSON.stringify(cacheKey)] = { __ts: Date.now(), __data: value };
          return value;
        });
      },
      delete(cacheKey) {
        return Promise.resolve().then(() => {
          delete cache[JSON.stringify(cacheKey)];
        });
      },
      clear() {
        return Promise.resolve().then(() => {
          Object.keys(cache).forEach((k) => delete cache[k]);
        });
      },
    };
  },

  /**
   * Create null cache (no caching — used when ?nocache is present)
   * @returns {object} Cache object that doesn't cache
   */
  createNullCache() {
    return {
      get(cacheKey, defaultValue, events = {}) {
        const value =
          typeof defaultValue === "function" ? defaultValue() : defaultValue;
        return Promise.resolve(value).then((resolvedValue) => {
          if (events.miss) {
            events.miss(resolvedValue);
          }
          return resolvedValue;
        });
      },
      set(cacheKey, value) {
        return Promise.resolve(value);
      },
      delete() {
        return Promise.resolve();
      },
      clear() {
        return Promise.resolve();
      },
    };
  },

  /**
   * Clear all Algolia cache entries from localStorage
   */
  clearAll() {
    const prefix = "algolia-responses-v";
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  },
};

// Atomic YMM clear sync — loaded globally via theme.liquid (every page incl. cart).
// WHY: the app YMM extension's clear fires YMM_FILTER_CLEAR but only removes
// `yme_result_vehicle` + the menu key; it LEAVES `garageSelected`, which the search and
// collection fitment readers fall back to — so a "removed" vehicle (e.g. a Ford) keeps
// silently filtering results. Wipe ALL vehicle keys + the ymm-* localStorage and bust the
// Algolia response cache so the next search reflects the cleared state.
document.addEventListener("YMM_FILTER_CLEAR", () => {
  try {
    ["yme_result_vehicle", "yme_collection_hierarchy_menu", "garageSelected"].forEach(
      (key) => sessionStorage.removeItem(key),
    );
    ["year", "make", "engine"].forEach((key) => localStorage.removeItem(`ymm-${key}`));
    if (typeof AlgoliaCacheConfig !== "undefined") AlgoliaCacheConfig.clearAll();
  } catch (error) {
    // best-effort — clearing is non-critical
  }
});
